import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import {
  Autocomplete,
  Box,
  Divider,
  Paper,
  Popper,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { ApiService } from "../../services/AppService";
import { Address, ResponseObject, ServiceRequest } from "../../classes/main";
import { getToken } from "../../services/HolderService";
import Cookies from "js-cookie";
import "./index.css";
import AddressLocationDialog from "../address/AddressLocationDialog";
import { Loader } from "../genericComponents/MainLoader/Loader";
import { trackEvent } from "@/app/utils/branch";
import { useLoadScript } from "@react-google-maps/api";
import type { Libraries } from "@googlemaps/js-api-loader";

interface GeoJSON {
  coordinates: [number, number];
}

interface AddressDetectLocation {
  id: string;
  name: string;
  geoJSON: GeoJSON;
}
interface LocationDialogProps {
  openDialog: any;
  setCloseDialog: any;
  handleAddressUpdate: any;
  reloadLocationFlag: any;
}
interface AutocompletePrediction {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
  terms: Array<{
    offset: number;
    value: string;
  }>;
  types: string[];
}

const API_KEY = "AIzaSyCY519Z3pdrcYUmVhOV8NftANVlYYoVRKQ";

const LocationDialog: React.FC<LocationDialogProps> = ({
  openDialog,
  setCloseDialog,
  handleAddressUpdate,
  reloadLocationFlag,
}) => {
  const [location, setLocation] = useState<{
    latitude: number | string | null;
    longitude: number | string | null;
  }>({
    latitude: null,
    longitude: null,
  });

  const [isShaking, setIsShaking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [addressDetectLocation, setAddressDetectLocation] = useState<
    AddressDetectLocation | undefined
  >(undefined);
  const [detectLocationFlag, setDetectLocationFlag] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [address, setAddress] = useState<Address>();
  const [predictions, setPredictions] = useState<AutocompletePrediction[]>([]);
  const token = getToken();
  const apiService: ApiService = useMemo(() => new ApiService(), []);
  const libraries = useRef<Libraries>(["places"]);
  const autocompleteService =
    useRef<google.maps.places.AutocompleteService | null>(null);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: API_KEY,
    libraries: libraries.current,
  });

  useEffect(() => {
    if (isLoaded && window.google) {
      autocompleteService.current =
        new google.maps.places.AutocompleteService();
    }
  }, [isLoaded]);

  const trackServiceNotFound = useCallback(() => {
    trackEvent("SERVICE_NOT_FOUND", null);
  }, []);

  useEffect(() => {
    if (location.latitude != null && location.longitude != null) {
      const serviceRequest: ServiceRequest = new ServiceRequest();
      serviceRequest.latitude = Number.parseFloat(
        location?.latitude.toString()
      );
      serviceRequest.longitude = Number.parseFloat(
        location.longitude.toString()
      );
      apiService
        .post(apiService.checkAvailability, serviceRequest, getToken()!)
        .then((value: unknown) => {
          const res = value as ResponseObject;
          if (res.status == "success" && res.object != null) {
            setLoading(true);
            localStorage.setItem(
              "serviceHub",
              btoa(JSON.stringify(res?.object))
            );
            localStorage.setItem(
              "serviceHubSlug",
              btoa(JSON.stringify(res?.object?.slug))
            );
            if (address) {
              localStorage.setItem("address", btoa(JSON.stringify(address)));
            }
            Cookies.set("serviceHub", res?.object?.id, {
              expires: 365,
              secure: true,
              sameSite: "Strict", // or 'Lax', depending on your use case
            });
            Cookies.set("serviceHubSlug", res?.object?.slug, {
              expires: 365,
              secure: true,
              sameSite: "Strict", // or 'Lax', depending on your use case
            });
            setCloseDialog(false);
            handleAddressUpdate();
            if (detectLocationFlag) {
              setAddressDetectLocation(res?.object);
            }
            window.location.reload();
          } else {
            alert(
              "Opps! We are not available at this location now. Please choose another location."
            );
            setLoading(false);
            trackServiceNotFound();
          }
        });
    }
  }, [location]);

  const getLocation = async () => {
    try {
      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          if ("geolocation" in navigator) {
            navigator.geolocation.getCurrentPosition(
              (position) => resolve(position),
              (error) => reject(error)
            );
          } else {
            reject(new Error("Geolocation is not supported by this browser."));
          }
        }
      );
      const { latitude, longitude } = position?.coords;
      setLocation({ latitude, longitude });
      const addressAvailable = localStorage.getItem("address");
      if (addressAvailable == null || undefined) {
        setDetectLocationFlag(true);
      }
    } catch (error: any) {
      console.error("Error obtaining location:", error);
      if (error?.code === 1) {
        alert(
          "Location access is required. We need access to your location to provide better service. Please enable location access: Click on the lock icon next to the URL bar. Change the Location setting to Allow."
        );
        setLoading(false);
      } else {
        alert(
          "Unable to retrieve your location. Please allow location access or enter the address manually."
        );
        setLoading(false);
      }
    }
  };

  // useEffect(() => {
  //   if (typeof window !== "undefined") {
  //     try {
  //       const serviceHubSlug = localStorage.getItem("serviceHubSlug");
  //       if (serviceHubSlug && isBase64(serviceHubSlug)) {
  //         const decodedSlug = JSON.parse(atob(serviceHubSlug));
  //         if (!decodedSlug) {
  //           getLocation();
  //         }
  //       } else {
  //         getLocation();
  //       }
  //     } catch (error) {
  //       getLocation();
  //     }
  //   }
  //   setReloadLocationFlag(false);
  // }, [reloadLocationFlag, setReloadLocationFlag]);

  // const isBase64 = (str: string) => {
  //   try {
  //     atob(str);
  //     return true;
  //   } catch {
  //     return false;
  //   }
  // };

  useEffect(() => {
    if (addressDetectLocation) {
      const address: Address = new Address();
      address.placeId = addressDetectLocation?.id;
      address.formattedAddress = addressDetectLocation?.name;
      address.savedAddress = false;
      address.latitude = addressDetectLocation?.geoJSON?.coordinates[0];
      address.longitude = addressDetectLocation?.geoJSON?.coordinates[1];
      localStorage.setItem("address", btoa(JSON.stringify(address)));
      handleAddressUpdate();
      setDetectLocationFlag(false);
    }
  }, [addressDetectLocation, handleAddressUpdate]);

  useEffect(() => {
    const locationData = localStorage.getItem("location");
    const coordinates = locationData ? JSON.parse(atob(locationData)) : null;
    if (coordinates !== null) {
      setLocation({
        latitude: coordinates?.[1],
        longitude: coordinates?.[0],
      });
      localStorage.removeItem("location");
    }
  }, []);

  useEffect(() => {
    const locationData = localStorage.getItem("serviceHub");
    if (locationData == null || locationData == undefined) {
      const serviceRequest: ServiceRequest = new ServiceRequest();
      serviceRequest.latitude = 28.7040592;
      serviceRequest.longitude = 77.10249019999999;
      apiService
        .post(apiService.checkAvailability, serviceRequest, getToken()!)
        .then((value: unknown) => {
          const res = value as ResponseObject;
          if (res.status == "success" && res.object != null) {
            localStorage.setItem(
              "serviceHub",
              btoa(JSON.stringify(res?.object))
            );
            localStorage.setItem(
              "serviceHubSlug",
              btoa(JSON.stringify(res?.object?.slug))
            );
            Cookies.set("serviceHub", res?.object?.id, {
              expires: 365,
              secure: true,
              sameSite: "Strict", // or 'Lax', depending on your use case
            });
            Cookies.set("serviceHubSlug", res?.object?.slug, {
              expires: 365,
              secure: true,
              sameSite: "Strict", // or 'Lax', depending on your use case
            });
            setAddressDetectLocation(res?.object);
          } else {
            alert(
              "Opps! We are not available at this location now. Please choose another location."
            );
            setLoading(false);
          }
        });
    }
  }, [apiService, reloadLocationFlag]);

  const handleShakeAnimation = () => {
    setIsShaking(true);
    setTimeout(() => setIsShaking(false), 500);
  };

  const handleCurrentLocation = () => {
    setLoading(true);
    setCloseDialog(false);
    setDetectLocationFlag(true);
    getLocation();
  };

  const getPlacePredictionsAsync = async (input: string) => {
    return new Promise<google.maps.places.AutocompletePrediction[]>(
      (resolve, reject) => {
        autocompleteService.current?.getPlacePredictions(
          { input, componentRestrictions: { country: ["IN", "NP"] } },
          (predictions, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK) {
              resolve(predictions || []);
            } else {
              reject(new Error("Failed to fetch predictions"));
            }
          }
        );
      }
    );
  };

  const handleInputChange = async (
    event: React.ChangeEvent<{}>,
    newInputValue: string
  ) => {
    setInputValue(newInputValue);
    try {
      if (autocompleteService.current && newInputValue) {
        const predictions = await getPlacePredictionsAsync(newInputValue);
        setPredictions(predictions);
      }
    } catch (error) {
      console.error(error);
      setPredictions([]);
    }
  };

  const handlePlaceChange = (event: React.ChangeEvent<{}>, newValue: any) => {
    if (!newValue || !newValue?.place_id) return;

    const placesService = new google.maps.places.PlacesService(
      document.createElement("div")
    );

    placesService?.getDetails(
      { placeId: newValue?.place_id },
      (placeDetails: google.maps.places.PlaceResult | null) => {
        if (placeDetails) {
          const address: Address = new Address();
          address.placeId = newValue?.place_id;
          address.formattedAddress = placeDetails?.formatted_address;
          address.savedAddress = false;

          address.latitude = placeDetails?.geometry?.location?.lat();
          address.longitude = placeDetails?.geometry?.location?.lng();
          setAddress(address);
          // localStorage.setItem("address", btoa(JSON.stringify(address)));
          setLocation({
            latitude: placeDetails?.geometry?.location?.lat() ?? null,
            longitude: placeDetails?.geometry?.location?.lng() ?? null,
          });
        }
      }
    );
  };

  const CustomPaper = styled(Paper)(({ theme }) => ({
    "&::-webkit-scrollbar": {
      display: "none",
    },
    msOverflowStyle: "none",
    scrollbarWidth: "none",
    width: "20rem",
    marginTop: "1rem",
  }));

  const CustomPopper = (props: any) => {
    return (
      <Popper
        {...props}
        style={{ ...props.style, width: "320px" }}
        placement="bottom-end"
      >
        {props.children}
      </Popper>
    );
  };

  return (
    <>
      {loading && <Loader />}
      <Dialog
        open={openDialog}
        onClose={handleShakeAnimation}
        className={isShaking ? "shake" : ""}
        role="dialog"
        aria-labelledby="location-dialog-title"
        aria-describedby="location-dialog-description"
        sx={{
          maxWidth: "35rem",
          padding: "1rem",
          "& .MuiBackdrop-root": {
            width: "120vw",
            marginLeft: "-2rem",
          },
          "@media (max-width: 600px)": {
            "& .MuiDialog-paper": {
              width: "100%",
              height: "100%",
              maxHeight: "100%",
              margin: 0,
            },
          },
        }}
        PaperProps={{
          sx: {
            position: "absolute",
            top: { xs: "auto", sm: "15%" },
            left: { xs: "auto", sm: "22%" },
            margin: { xs: "0px 1rem", sm: "0px" },
            bottom: { xs: "0px", sm: "1rem" },
            overflowY: "hidden",
            height: "fit-content",
            maxHeight: "calc(100% - 200px)",
          },
        }}
      >
        <DialogContent
          sx={{
            display: "flex",
            overflowY: "visible",
            padding: "1rem",
            justifyContent: "left",
            alignItems: "center",
            height: "fit-content",
            maxHeight: "fit-content",
          }}
        >
          <LocationOnIcon style={{ color: "#005200", fontSize: "40px" }} />
          <DialogContentText
            id="alert-dialog-title"
            style={{ paddingLeft: "10px", fontSize: "14px" }}
          >
            Please provide your location to check the services avalibility in
            your area.
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: "space-evenly",
            padding: "1.2rem",
            display: { xs: "block", sm: "flex" },
            "& > :not(style) ~ :not(style)": {
              marginLeft: { xs: "0px", sm: "8px" },
            },
          }}
        >
          <Button
            onClick={handleCurrentLocation}
            variant="contained"
            sx={{
              textTransform: "capitalize",
              padding: "15px 0px",
              background: "#f0ca69",
              boxShadow: "none",
              color: "black",
              textWrap: "nowrap",
              width: "100%",
              maxWidth: "10rem",
              marginBottom: { xs: "8px", sm: "0px" },
              minWidth: { xs: "100%", sm: "auto" },
              "&:hover": {
                background: "#f0ca69",
              },
            }}
          >
            Detect my location
          </Button>
          {isLoaded && (
            <Autocomplete
              freeSolo
              inputValue={inputValue}
              options={predictions}
              onInputChange={handleInputChange}
              getOptionLabel={(option: any) => option.description ?? ""}
              onChange={handlePlaceChange} // Using the corrected handler
              PaperComponent={(props) => <CustomPaper {...props} />}
              PopperComponent={CustomPopper}
              renderOption={(props: any, option: any) => {
                const { key, ...restProps } = props;
                return (
                  <Box
                    key={option?.place_id}
                    component="li"
                    {...restProps}
                    sx={{
                      flexDirection: "column",
                      width: "100%",
                      float: "left",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        textAlign: "left",
                        width: "100%",
                      }}
                    >
                      {option?.description ?? ""}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        textAlign: "left",
                        width: "100%",
                        fontSize: "11px",
                      }}
                    >
                      {option?.structured_formatting?.main_text ?? ""}
                    </Typography>
                  </Box>
                );
              }}
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor: "#005200",
                  },
              }}
              renderInput={(params) => (
                <TextField {...params} label="Search Places" />
              )}
            />
          )}
        </DialogActions>
        <Divider />
        {token && (
          <DialogContent
            sx={{
              overflowY: "auto",
              padding: "1rem",
              height: "fit-content",
              maxHeight: "fit-content",
            }}
          >
            <AddressLocationDialog
              setCloseDialog={() => {
                setCloseDialog(false);
              }}
              setLocation={setLocation}
              setLoading={() => setLoading(true)}
            />
          </DialogContent>
        )}
      </Dialog>
    </>
  );
};

export default LocationDialog;
